/**
 * Shipyard enum and display config.
 * Names and initials are used in dropdowns and Project Explorer.
 * CI images are loaded from src/assets by filename (e.g. "Hanwha Ocean.png").
 */

export const SHIPYARD_OPTIONS = [
  { name: 'Hanwha Ocean', initials: 'HO' },
  { name: 'HD Hyundai Heavy Industries', initials: 'HHI' },
  { name: 'HJ Shipbuilding and Construction', initials: 'HJ SC' },
  { name: 'HD Hyundai Samho', initials: 'HSHI' },
  { name: 'K Shipbuilding', initials: 'K Shipbuilding' },
  { name: 'Korea Yanase Tongyeong Shipbuilding', initials: 'KYTS' },
  { name: 'Samsung Heavy Industries', initials: 'SHI' },
] as const

export type ShipyardName = (typeof SHIPYARD_OPTIONS)[number]['name']

/** Display label for dropdown: "Name (Initials)" */
export function getShipyardLabel(name: string): string {
  const opt = SHIPYARD_OPTIONS.find((o) => o.name === name)
  return opt ? `${name} (${opt.initials})` : name
}

/** Get initials for a shipyard name */
export function getShipyardInitials(name: string): string {
  const opt = SHIPYARD_OPTIONS.find((o) => o.name === name)
  return opt ? opt.initials : name
}

/** All shipyard names for dropdown value list */
export const SHIPYARD_NAMES: string[] = SHIPYARD_OPTIONS.map((o) => o.name)

/** Sort API shipyard rows to match canonical enum order; unknown names last. */
export function sortShipyardsByEnum<T extends { name: string }>(rows: T[]): T[] {
  const order = new Map(SHIPYARD_NAMES.map((n, i) => [n, i]))
  return [...rows].sort((a, b) => {
    const na = normalizeShipyardDisplay(a.name) || a.name
    const nb = normalizeShipyardDisplay(b.name) || b.name
    const ia = order.has(na) ? order.get(na)! : 999
    const ib = order.has(nb) ? order.get(nb)! : 999
    if (ia !== ib) return ia - ib
    return a.name.localeCompare(b.name)
  })
}

/**
 * Resolve shipyard display name from stored value (name or initials).
 * Used when data might have been stored as initials (e.g. HHI) or full name.
 */
export function normalizeShipyardDisplay(value: string | null | undefined): string {
  if (!value || !value.trim()) return ''
  const v = value.trim()
  const byName = SHIPYARD_OPTIONS.find((o) => o.name === v)
  if (byName) return byName.name
  const byInitials = SHIPYARD_OPTIONS.find((o) => o.initials === v)
  if (byInitials) return byInitials.name
  return v
}

// Build map of shipyard name -> image URL from src/assets (CI logos)
// Add PNGs named exactly: "Hanwha Ocean.png", "HD Hyundai Heavy Industries.png",
// "HJ Shipbuilding and Construction.png", "HD Hyundai Samho.png", "K Shipbuilding.png", "Samsung Heavy Industries.png"
const shipyardImageMap: Record<string, string> = {}
try {
  const glob = import.meta.glob<string>('@/assets/*.png', { eager: true })
  for (const [path, mod] of Object.entries(glob)) {
    const base = path.split('/').pop() || path
    const name = base.replace(/\.png$/i, '')
    const url = typeof mod === 'object' && mod != null && 'default' in mod ? (mod as { default: string }).default : (mod as string)
    if (url) shipyardImageMap[name] = url
  }
} catch {
  // ignore
}

// Optional alias: display name -> asset filename (when file is named differently)
const imageNameAlias: Record<string, string> = {
  'HD Hyundai Samho': 'Hyundai Samho',
  'HD Hyundai Heavy Industries': 'HHI',
}

/** Get CI image URL for a shipyard name; undefined if not in assets */
export function getShipyardImageSrc(shipyardName: string): string | undefined {
  const normalized = normalizeShipyardDisplay(shipyardName) || shipyardName
  const assetName = imageNameAlias[normalized] ?? normalized
  return shipyardImageMap[assetName] ?? shipyardImageMap[normalized]
}
